import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { BarLoader } from "react-spinners";
import { motion } from "framer-motion";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const Signin = () => {
  const [registrationId, setRegistrationId] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  axios.defaults.withCredentials = true;

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoaded(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const loginResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/users/login`,
        { registrationId, email, password },
        { withCredentials: true }
      );

      if (loginResponse.data.success) {
        const meResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/me`,
          { withCredentials: true }
        );
        if (meResponse.data.success) {
          const { role } = meResponse.data;
          if (role === "admin") {
            navigate("/admin/dashboard");
          } else if (role === "cso") {
            navigate("/user/dashboard");
          } else {
            setMessage("Unknown role: " + role);
          }
        } else {
          setMessage("Failed to fetch user details.");
        }
      } else {
        setMessage(loginResponse.data.message);
      }
    } catch (error) {
      console.error("Error during login:", error);
      setMessage(
        error.response?.data?.message || "An unexpected error occurred."
      );
    } finally {
      setLoading(false);
    }
  };

  if (loaded) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-white">
        <BarLoader color="#4F46E5" height={2} width={200} />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-14 md:p-16 mb-24 flex items-center justify-center bg-gradient-to-br from-indigo-50 to-white">
      <div className="w-full max-w-4xl flex flex-col md:flex-row rounded-2xl overflow-hidden shadow-xl">
        {/* Left Panel - Branding */}
        <div className="w-full md:w-1/2 bg-gradient-to-r from-indigo-600 to-indigo-800 p-10 flex flex-col justify-between">
          <div>
            <div className="flex items-center mb-8">
              <img
                src="/logo3.png"
                alt="Company Logo"
                className="h-16 w-16 mr-4 bg-white rounded-full p-2"
              />
              <h1 className="text-2xl font-bold text-white">
                Bishoftu Finance Office
              </h1>
            </div>

            <h2 className="text-3xl font-bold text-white mb-4">
              Secure Financial Management
            </h2>
            <p className="text-indigo-100 max-w-md">
              Access your financial dashboard and manage your accounts with our
              secure platform. Trusted by financial professionals since 2025.
            </p>
          </div>

          <div className="mt-8">
            <div className="flex items-center text-indigo-200 mb-4">
              <div className="h-10 w-10 rounded-full bg-indigo-500 flex items-center justify-center mr-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <p>Secure</p>
            </div>

            <div className="flex items-center text-indigo-200">
              <div className="h-10 w-10 rounded-full bg-indigo-500 flex items-center justify-center mr-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <p>Real-time</p>
            </div>
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div className="w-full md:w-1/2 bg-white p-8 md:p-12 flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold text-gray-800 mb-2">
              Welcome Back
            </h2>
            <p className="text-gray-500 mb-8">
              Sign in to your account to continue
            </p>
            <p className="text-gray-500 mb-8">
              Please note that, this Login Page is Only for CSO users
            </p>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label
                  htmlFor="registrationId"
                  className="block text-gray-700 font-medium mb-2"
                >
                  Registration ID
                </label>
                <input
                  type="text"
                  id="registrationId"
                  value={registrationId}
                  onChange={(e) => setRegistrationId(e.target.value)}
                  placeholder="Enter your registration ID"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-gray-700 font-medium mb-2"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                />
              </div>

              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  type="button"
                  className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <FaEyeSlash className="h-5 w-5" />
                  ) : (
                    <FaEye className="h-5 w-5" />
                  )}
                </button>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="remember-me"
                    className="ml-2 block text-sm text-gray-700"
                  >
                    Remember me
                  </label>
                </div>

                <Link
                  to="/forgot_password"
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                >
                  Forgot Password?
                </Link>
              </div>
              {message && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-3 rounded-lg bg-red-50 text-red-700 text-center"
                >
                  {message}
                </motion.div>
              )}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className={`w-full py-3 px-4 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition ${
                  loading
                    ? "bg-indigo-400 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg"
                }`}
              >
                {loading ? (
                  <div className="flex justify-center items-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Signing in...
                  </div>
                ) : (
                  "Sign In"
                )}
              </motion.button>
            </form>

            <div className="mt-8 text-center">
              <p className="text-gray-600">
                Don't have an account?{" "}
                <Link
                  to="/user/signup"
                  className="font-medium text-indigo-600 hover:text-indigo-500"
                >
                  Create account
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Signin;
